/**
 * Small hand-built SVG/HTML charts for the engagement dashboard. The project
 * has no chart library and the brief prefers not adding a dependency.
 *
 * Every chart shows values in a shared tooltip on hover, tap or keyboard focus
 * (arrow keys move, Escape hides) and announces them in a polite live region.
 */

import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type Dispatch,
  type FocusEvent,
  type KeyboardEvent,
  type PointerEvent,
  type RefObject,
  type SetStateAction,
} from 'react';
import clsx from 'clsx';
import { dayLabel, nearestIndex, placeBeside, placeCentred, stepIndex, weekdayName } from '@/utils/engagement';

export const SERIES_COLOURS = ['#2563eb', '#16a34a', '#d97706', '#dc2626', '#7c3aed', '#0891b2', '#db2777', '#65a30d'];
export const OTHER_COLOUR = '#94a3b8';

const shortDate = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });

// ---------------------------------------------------------------------------
// Shared tooltip
// ---------------------------------------------------------------------------

interface TipContent {
  title: string;
  rows: { label?: string; value: string; colour?: string }[];
}

const describeTip = ({ title, rows }: TipContent) =>
  `${title}: ${rows.map((r) => (r.label ? `${r.label} ${r.value}` : r.value)).join(', ')}`;

const FOCUS_RING =
  'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500';

function ChartTip({
  tipRef,
  content,
  left,
  top,
}: {
  tipRef: RefObject<HTMLDivElement>;
  content: TipContent;
  left: number | null;
  top: number | null;
}) {
  return (
    <div
      ref={tipRef}
      role="tooltip"
      className="pointer-events-none absolute z-20 whitespace-nowrap rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs shadow-lg"
      style={{ left: left ?? 0, top: top ?? 0, visibility: left === null || top === null ? 'hidden' : 'visible' }}
    >
      <p className="font-semibold text-gray-900">{content.title}</p>
      {content.rows.map((row) => (
        <p
          key={row.label ?? row.value}
          className="mt-0.5 flex items-center gap-1.5 font-medium"
          style={{ color: row.colour ?? '#374151' }}
        >
          {row.colour && <span className="inline-block h-2 w-2 flex-shrink-0 rounded-sm" style={{ backgroundColor: row.colour }} />}
          {row.label && <span>{row.label}</span>}
          <span className="ml-auto pl-2 tabular-nums">{row.value}</span>
        </p>
      ))}
    </div>
  );
}

function LiveRegion({ text }: { text: string }) {
  return (
    <p className="sr-only" aria-live="polite">
      {text}
    </p>
  );
}

/** Keyboard focus only (a mouse click or tap also focuses the chart). */
const isFocusVisible = (el: Element) => {
  try {
    return el.matches(':focus-visible');
  } catch {
    return true;
  }
};

/**
 * The active item of a chart, with keyboard navigation, hiding on blur, on
 * Escape, and on a tap outside the chart (touch has no pointer leave).
 */
function useActiveIndex(count: number, { columns, initial = 'first' }: { columns?: number; initial?: 'first' | 'last' } = {}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [stored, setActive] = useState<number | null>(null);
  const active = stored !== null && stored < count ? stored : null;
  const initialIndex = initial === 'last' ? count - 1 : 0;
  useDismissOnOutside(wrapRef, active !== null, setActive);

  const focusProps = {
    tabIndex: 0,
    onKeyDown: (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (active === null) return;
        e.preventDefault();
        setActive(null);
        return;
      }
      const next =
        active === null && e.key.startsWith('Arrow') && count > 0
          ? initialIndex
          : stepIndex(active, e.key, count, columns);
      if (next === null) return;
      e.preventDefault();
      setActive(next);
    },
    onFocus: (e: FocusEvent) => {
      if (active === null && count > 0 && isFocusVisible(e.currentTarget)) setActive(initialIndex);
    },
    onBlur: () => setActive(null),
  };

  return { wrapRef, active, setActive, focusProps };
}

function useDismissOnOutside(
  ref: RefObject<HTMLElement>,
  open: boolean,
  setActive: Dispatch<SetStateAction<number | null>>,
) {
  useEffect(() => {
    if (!open) return;
    const dismiss = (e: globalThis.PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setActive(null);
    };
    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, [open, ref, setActive]);
}

/** Pointer handlers for one item of a bar, slice or cell chart. */
function itemHandlers(index: number, setActive: Dispatch<SetStateAction<number | null>>) {
  return {
    onPointerEnter: () => setActive(index),
    onPointerDown: () => setActive(index),
    onPointerLeave: (e: PointerEvent) => {
      if (e.pointerType === 'mouse') setActive((current) => (current === index ? null : current));
    },
  };
}

/**
 * Tooltip position above the active item (below it when that would leave the
 * screen), clamped to the chart's width. The anchor is the element marked
 * data-tip-anchor="<index>", else data-tip="<index>".
 */
function useItemTipPosition(
  wrapRef: RefObject<HTMLDivElement>,
  tipRef: RefObject<HTMLDivElement>,
  active: number | null,
) {
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const tip = tipRef.current;
    const anchor =
      active === null
        ? null
        : wrap?.querySelector(`[data-tip-anchor="${active}"]`) ?? wrap?.querySelector(`[data-tip="${active}"]`);
    if (!wrap || !tip || !anchor) {
      setPos(null);
      return;
    }
    const box = wrap.getBoundingClientRect();
    const target = anchor.getBoundingClientRect();
    const fitsAbove = target.top - tip.offsetHeight - 6 >= 0;
    setPos({
      left: placeCentred(target.left + target.width / 2 - box.left, tip.offsetWidth, box.width),
      top: fitsAbove ? target.top - box.top - tip.offsetHeight - 6 : target.bottom - box.top + 6,
    });
  }, [wrapRef, tipRef, active]);
  return pos;
}

// ---------------------------------------------------------------------------
// Line chart
// ---------------------------------------------------------------------------

export interface LineSeries {
  label: string;
  colour: string;
  values: number[];
}

export function LineChart({ dates, series, label }: { dates: string[]; series: LineSeries[]; label: string }) {
  const W = 640;
  const H = 220;
  const pad = { top: 12, right: 12, bottom: 28, left: 36 };
  const innerW = W - pad.left - pad.right;
  const innerH = H - pad.top - pad.bottom;
  const max = Math.max(1, ...series.flatMap((s) => s.values));
  const x = (i: number) => pad.left + (dates.length <= 1 ? innerW / 2 : (i / (dates.length - 1)) * innerW);
  const y = (v: number) => pad.top + innerH - (v / max) * innerH;
  const ticks = [0, Math.round(max / 2), max].filter((v, i, a) => a.indexOf(v) === i);
  const xLabels = dates.length <= 1 ? [0] : [0, Math.floor((dates.length - 1) / 2), dates.length - 1];
  const showPoints = dates.length <= 62;

  const hintId = useId();
  const svgRef = useRef<SVGSVGElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const { wrapRef, active, setActive, focusProps } = useActiveIndex(dates.length, { initial: 'last' });
  const [tipLeft, setTipLeft] = useState<number | null>(null);

  const content: TipContent | null =
    active === null
      ? null
      : {
          title: dayLabel(dates[active]),
          rows: series.map((s) => ({ label: s.label, value: String(s.values[active] ?? 0), colour: s.colour })),
        };

  const indexAt = (clientX: number) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect?.width) return null;
    return nearestIndex(((clientX - rect.left) / rect.width) * W, dates.length, pad.left, innerW);
  };

  useLayoutEffect(() => {
    const rect = svgRef.current?.getBoundingClientRect();
    const tip = tipRef.current;
    if (active === null || !rect || !tip) {
      setTipLeft(null);
      return;
    }
    setTipLeft(placeBeside((x(active) / W) * rect.width, tip.offsetWidth, rect.width));
    // x() changes only with dates.length.
  }, [active, dates.length]);

  return (
    <div>
      <ul className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
        {series.map((s) => (
          <li key={s.label} className="flex items-center gap-1.5">
            <span className="inline-block h-2 w-4 rounded-sm" style={{ backgroundColor: s.colour }} />
            {s.label}
          </li>
        ))}
      </ul>
      <div ref={wrapRef} className="relative">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          className={clsx('h-auto w-full cursor-crosshair rounded', FOCUS_RING)}
          style={{ touchAction: 'pan-y' }}
          role="img"
          aria-label={label}
          aria-describedby={hintId}
          {...focusProps}
          onPointerMove={(e) => setActive(indexAt(e.clientX))}
          onPointerDown={(e) => setActive(indexAt(e.clientX))}
          onPointerLeave={(e) => {
            if (e.pointerType === 'mouse') setActive(null);
          }}
        >
          {ticks.map((t) => (
            <g key={t}>
              <line x1={pad.left} x2={W - pad.right} y1={y(t)} y2={y(t)} stroke="#e5e7eb" />
              <text x={pad.left - 6} y={y(t) + 4} textAnchor="end" fontSize="11" fill="#6b7280">
                {t}
              </text>
            </g>
          ))}
          {xLabels.map((i) => (
            <text key={i} x={x(i)} y={H - 8} textAnchor="middle" fontSize="11" fill="#6b7280">
              {dates[i] ? shortDate(dates[i]) : ''}
            </text>
          ))}
          {active !== null && (
            <line
              x1={x(active)}
              x2={x(active)}
              y1={pad.top}
              y2={pad.top + innerH}
              stroke="#9ca3af"
              strokeDasharray="3 3"
              data-testid="chart-guide"
            />
          )}
          {series.map((s) => (
            <g key={s.label}>
              <polyline
                fill="none"
                stroke={s.colour}
                strokeWidth={2}
                strokeLinejoin="round"
                points={s.values.map((v, i) => `${x(i)},${y(v)}`).join(' ')}
              />
              {showPoints && s.values.map((v, i) => <circle key={i} cx={x(i)} cy={y(v)} r={2.5} fill={s.colour} />)}
            </g>
          ))}
          {active !== null &&
            series.map((s) => (
              <circle
                key={s.label}
                cx={x(active)}
                cy={y(s.values[active] ?? 0)}
                r={4.5}
                fill={s.colour}
                stroke="#ffffff"
                strokeWidth={2}
              />
            ))}
        </svg>
        {content && <ChartTip tipRef={tipRef} content={content} left={tipLeft} top={4} />}
        <p id={hintId} className="sr-only">
          Use the left and right arrow keys to read each day&apos;s values.
        </p>
        <LiveRegion text={content ? describeTip(content) : ''} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Horizontal bar list
// ---------------------------------------------------------------------------

export function BarList({
  items,
  format = (v) => String(v),
  colour = '#2563eb',
  label,
}: {
  items: { label: string; value: number; hint?: string }[];
  format?: (v: number) => string;
  colour?: string;
  label: string;
}) {
  const max = Math.max(1, ...items.map((i) => i.value));
  const tipRef = useRef<HTMLDivElement>(null);
  const { wrapRef, active, setActive, focusProps } = useActiveIndex(items.length);
  const pos = useItemTipPosition(wrapRef, tipRef, active);
  const content: TipContent | null =
    active === null ? null : { title: items[active].label, rows: [{ value: format(items[active].value), colour }] };

  return (
    <div ref={wrapRef} className="relative">
      <ul className={clsx('space-y-2 rounded', FOCUS_RING)} aria-label={label} {...focusProps}>
        {items.map((item, i) => (
          <li key={item.label} className="text-sm" data-tip={i} {...itemHandlers(i, setActive)}>
            <div className="mb-0.5 flex items-baseline justify-between gap-3">
              <span className={clsx('truncate', item.value === 0 ? 'text-gray-400' : 'text-gray-700')} title={item.hint}>
                {item.label}
              </span>
              <span className="tabular-nums text-gray-900">{format(item.value)}</span>
            </div>
            <div className={clsx('h-2 rounded-full', active === i ? 'bg-gray-200' : 'bg-gray-100')}>
              <div
                data-tip-anchor={i}
                className="h-2 rounded-full"
                style={{ width: `${(item.value / max) * 100}%`, backgroundColor: colour }}
              />
            </div>
          </li>
        ))}
      </ul>
      {content && <ChartTip tipRef={tipRef} content={content} left={pos?.left ?? null} top={pos?.top ?? null} />}
      <LiveRegion text={content ? describeTip(content) : ''} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Weekday × hour heatmap
// ---------------------------------------------------------------------------

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];
const hourLabel = (h: number) => `${String(h).padStart(2, '0')}:00`;

export function Heatmap({ cells, label }: { cells: { weekday: number; hour: number; events: number }[]; label: string }) {
  const max = Math.max(1, ...cells.map((c) => c.events));
  const value = (weekday: number, hour: number) =>
    cells.find((c) => c.weekday === weekday && c.hour === hour)?.events ?? 0;
  const tipRef = useRef<HTMLDivElement>(null);
  const { wrapRef, active, setActive, focusProps } = useActiveIndex(WEEKDAYS.length * HOURS.length, {
    columns: HOURS.length,
  });
  const pos = useItemTipPosition(wrapRef, tipRef, active);

  const content: TipContent | null = (() => {
    if (active === null) return null;
    const wd = Math.floor(active / HOURS.length) + 1;
    const h = active % HOURS.length;
    const v = value(wd, h);
    return {
      title: `${weekdayName(wd)} ${hourLabel(h)}–${hourLabel((h + 1) % 24)}`,
      rows: [{ value: `${v} event${v === 1 ? '' : 's'}`, colour: '#2563eb' }],
    };
  })();

  return (
    <div ref={wrapRef} className="relative">
      <div className={clsx('overflow-x-auto rounded', FOCUS_RING)} role="group" aria-label={label} {...focusProps}>
        <table className="w-full min-w-[560px] border-separate text-[10px] text-gray-500" style={{ borderSpacing: 2 }}>
          <thead>
            <tr>
              <th className="w-8" />
              {HOURS.map((h) => (
                <th key={h} className="font-normal">
                  {h % 3 === 0 ? String(h).padStart(2, '0') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {WEEKDAYS.map((wd) => (
              <tr key={wd}>
                <th className="pr-1 text-left font-normal">{weekdayName(wd)}</th>
                {HOURS.map((h) => {
                  const v = value(wd, h);
                  const index = (wd - 1) * HOURS.length + h;
                  return (
                    <td
                      key={h}
                      className={clsx('h-5 rounded-sm', active === index && 'outline outline-2 outline-gray-900')}
                      style={{
                        backgroundColor: v === 0 ? '#f3f4f6' : `rgba(37, 99, 235, ${0.15 + 0.85 * (v / max)})`,
                      }}
                      data-tip={index}
                      {...itemHandlers(index, setActive)}
                    />
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {content && <ChartTip tipRef={tipRef} content={content} left={pos?.left ?? null} top={pos?.top ?? null} />}
      <LiveRegion text={content ? describeTip(content) : ''} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Donut chart
// ---------------------------------------------------------------------------

export function DonutChart({ slices, label }: { slices: { label: string; value: number }[]; label: string }) {
  const total = slices.reduce((s, x) => s + x.value, 0);
  const R = 60;
  const r = 36;
  const colourFor = (i: number, l: string) => (l === 'Other' ? OTHER_COLOUR : SERIES_COLOURS[i % SERIES_COLOURS.length]);
  const pct = (v: number) => (total ? Math.round((v / total) * 100) : 0);

  let angle = -Math.PI / 2;
  const arcs = slices.map((s, i) => {
    const sweep = total ? (s.value / total) * Math.PI * 2 : 0;
    const start = angle;
    angle += sweep;
    return { ...s, start, end: angle, colour: colourFor(i, s.label) };
  });

  const coords = (radius: number, a: number) => [70 + radius * Math.cos(a), 70 + radius * Math.sin(a)];
  const point = (radius: number, a: number) => coords(radius, a).join(' ');
  const path = (start: number, end: number) => {
    const large = end - start > Math.PI ? 1 : 0;
    return `M ${point(R, start)} A ${R} ${R} 0 ${large} 1 ${point(R, end)} L ${point(r, end)} A ${r} ${r} 0 ${large} 0 ${point(r, start)} Z`;
  };

  const tipRef = useRef<HTMLDivElement>(null);
  const { wrapRef, active, setActive, focusProps } = useActiveIndex(arcs.length);
  const pos = useItemTipPosition(wrapRef, tipRef, active);
  const content: TipContent | null =
    active === null
      ? null
      : {
          title: arcs[active].label,
          rows: [{ value: `${arcs[active].value} (${pct(arcs[active].value)}%)`, colour: arcs[active].colour }],
        };

  return (
    <div ref={wrapRef} className="relative flex flex-col items-center gap-4 sm:flex-row sm:items-start">
      <svg
        viewBox="0 0 140 140"
        className={clsx('h-36 w-36 flex-shrink-0 rounded-full', FOCUS_RING)}
        style={{ touchAction: 'manipulation' }}
        role="img"
        aria-label={label}
        {...focusProps}
      >
        {arcs.length === 1 ? (
          <circle
            cx={70}
            cy={70}
            r={(R + r) / 2}
            fill="none"
            stroke={arcs[0].colour}
            strokeWidth={R - r}
            data-tip={0}
            {...itemHandlers(0, setActive)}
          />
        ) : (
          arcs.map((a, i) => (
            <path
              key={a.label}
              d={path(a.start, a.end)}
              fill={a.colour}
              opacity={active === null || active === i ? 1 : 0.55}
              data-tip={i}
              {...itemHandlers(i, setActive)}
            />
          ))
        )}
        {arcs.map((a, i) => {
          // Anchor the tooltip on the middle of the slice's ring, not its bounding box.
          const [cx, cy] = arcs.length === 1 ? coords((R + r) / 2, -Math.PI / 2) : coords((R + r) / 2, (a.start + a.end) / 2);
          return <circle key={a.label} cx={cx} cy={cy} r={0.5} fill="none" pointerEvents="none" data-tip-anchor={i} />;
        })}
        <text x={70} y={74} textAnchor="middle" fontSize="14" fontWeight="600" fill="#111827" pointerEvents="none">
          {total}
        </text>
      </svg>
      <ul className="w-full min-w-0 space-y-1 text-sm">
        {arcs.map((a) => (
          <li key={a.label} className="flex items-center gap-2">
            <span className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-sm" style={{ backgroundColor: a.colour }} />
            <span className="min-w-0 flex-1 truncate text-gray-700">{a.label}</span>
            <span className="tabular-nums text-gray-900">{a.value}</span>
            <span className="w-10 text-right tabular-nums text-gray-500">{pct(a.value)}%</span>
          </li>
        ))}
      </ul>
      {content && <ChartTip tipRef={tipRef} content={content} left={pos?.left ?? null} top={pos?.top ?? null} />}
      <LiveRegion text={content ? describeTip(content) : ''} />
    </div>
  );
}
